/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useRef, useState } from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type HeaderElement,
	type ParsedElement,
	type SectionElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';

import { OneColumnLayout } from './section-layout/one-column-layout';

type FoldedStyleProps = {
	section: SectionElement;
	header: HeaderElement | undefined;
	content: ParsedElement[];
	children?: React.ReactNode;
};

const unboundedStyles = unboundedCssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		marginTop: token('space.100'),
		gap: token('space.100'),
		width: '100%',
	},
	contentWrapper: {
		overflow: 'hidden',
		paddingLeft: token('space.300'),
	},
	contentExpanded: {
		maxHeight: '500px',
	},
	contentCollapsed: {
		maxHeight: '22px',
		overflow: 'hidden',
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 1,
	},
});

const styles = cssMap({
	divider: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
});

export const DynamicLayoutFoldableSection = ({
	section,
	header,
	content,
	children,
}: FoldedStyleProps): React.JSX.Element => {
	const containerRef = useRef(null);
	const [isFolded, setIsFolded] = useState<boolean>(section.attributes?.folded === '1');
	const sectionKey = `${section.attributes?.layout || 'default'}-${header?.children || ''}`;

	return (
		<div css={unboundedStyles.root} data-testid="dynamic-layout-foldable-section">
			<Inline alignBlock="center">
				<IconButton
					icon={isFolded ? ChevronDownIcon : ChevronUpIcon}
					label={`${isFolded ? 'expand' : 'collapsed'} section`}
					appearance="subtle"
					spacing="compact"
					onClick={() => setIsFolded(!isFolded)}
					testId={isFolded ? 'expand-button' : 'collapse-button'}
					aria-expanded={!isFolded}
				/>

				{header && header.attributes?.show !== 'false' && content && (
					<Text weight="bold">{header && (header.children as string)}</Text>
				)}
			</Inline>
			<div
				ref={containerRef}
				css={[
					unboundedStyles.contentWrapper,
					isFolded ? unboundedStyles.contentCollapsed : unboundedStyles.contentExpanded,
				]}
			>
				{isFolded ? (
					<OneColumnLayout key={`${sectionKey}-collapsed`} elements={content} />
				) : (
					<Box key={`${sectionKey}-expanded`}>{children}</Box>
				)}
			</div>
			<Box xcss={styles.divider} />
		</div>
	);
};
